import type { ComponentType, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, Route, Routes, matchPath, useLocation } from "react-router";
import type { Me, Role } from "../api/generated/model";
import { LoginPage } from "../features/auth/LoginPage";
import { MfaChallengePage } from "../features/auth/MfaChallengePage";
import { MfaEnrollmentPage } from "../features/auth/MfaEnrollmentPage";
import { useSession, type SessionState } from "../features/auth/useSession";
import { AdminHome } from "../features/dashboard/AdminHome";
import { StudentHome } from "../features/dashboard/StudentHome";
import { TeacherHome } from "../features/dashboard/TeacherHome";
import { ExecutiveDashboard } from "../features/dashboard/ExecutiveDashboard";
import { SchedulePage } from "../features/schedule/SchedulePage";
import { ScheduleChangesPage } from "../features/schedule/ScheduleChangesPage";
import { ClassSeriesPage } from "../features/series/ClassSeriesPage";
import { RoomPage } from "../features/room/RoomPage";
import { RecordingsPage } from "../features/recordings/RecordingsPage";
import { MakeupPage } from "../features/makeup/MakeupPage";
import { MakeupRespondPage } from "../features/makeup/MakeupRespondPage";
import { AttendancePage } from "../features/attendance/AttendancePage";
import { AttendanceAdjustPage } from "../features/attendance/AttendanceAdjustPage";
import { ClassRecordForm } from "../features/academic/ClassRecordForm";
import {
  StudentPerformance,
  StudentPerformanceForStudent,
} from "../features/academic/StudentPerformance";
import { AssessmentSettings } from "../features/academic/AssessmentSettings";
import { MaterialLibrary } from "../features/materials/MaterialLibrary";
import { Inbox } from "../features/messaging/Inbox";
import { Announcements } from "../features/messaging/Announcements";
import { ReminderPreferences } from "../features/settings/ReminderPreferences";
import { StudentsPage } from "../features/enrollment/StudentsPage";
import { StudentDetailPage } from "../features/enrollment/StudentDetailPage";
import { TeachersPage } from "../features/enrollment/TeachersPage";
import { TeacherDetailPage } from "../features/enrollment/TeacherDetailPage";
import { PlansPage } from "../features/enrollment/PlansPage";
import { EnrollmentsPage } from "../features/enrollment/EnrollmentsPage";
import { MyEnrollmentPage } from "../features/enrollment/MyEnrollmentPage";
import { StudentBilling } from "../features/billing/StudentBilling";
import { AdminFinance } from "../features/billing/AdminFinance";
import { PricingPage } from "../features/pricing/PricingPage";
import { TeacherEarnings } from "../features/payroll/TeacherEarnings";
import { PayrollBatchesPage, PayrollBatchPage } from "../features/payroll/PayrollBatch";
import { TermsGate } from "../features/terms/TermsGate";
import { AppShell } from "./AppShell";
import dashboard from "../features/dashboard/dashboard.module.css";
import { navItemsFor } from "./nav";
import { ROLE_HOME_PATH, RoleGuard } from "./RoleGuard";

function FullPageMessage({ children }: { readonly children: ReactNode }) {
  return (<main><p>{children}</p></main>);
}

function Loading() {
  const { t } = useTranslation();
  return <FullPageMessage>{t("app.loading")}</FullPageMessage>;
}

/**
 * Painel do suporte.
 *
 * O suporte nao tem matricula, agenda nem fatura, e por isso ele NAO ganha o
 * painel do aluno com os cartoes vazios: cartao vazio se le como carga travada
 * ou como conta sem dado, e nenhuma das duas leituras e verdade. O que a tela
 * diz e a unica coisa que ela sabe com certeza — o que este papel alcanca.
 *
 * Os destinos saem de `navItemsFor`, e nao de uma segunda lista escrita aqui: o
 * menu lateral ja e a resposta curada para "o que o suporte faz na plataforma",
 * e duas listas divergiriam no dia em que uma delas mudasse. `ROLE_HOME_PATH`
 * sai da lista porque e esta propria tela.
 *
 * O titulo e `role.support`, e nao uma frase nova: o catalogo de traducao
 * atravessa as tres trilhas e nao e desta onda. Quando houver painel de verdade
 * para o suporte — a fila de conversas dele e o candidato obvio —, ele entra no
 * lugar desta lista sem que nenhum outro arquivo precise mudar.
 */
function SupportHome() {
  const { t } = useTranslation();
  const destinations = navItemsFor("support").filter((item) => item.to !== ROLE_HOME_PATH);

  return (
    <>
      <h1 className={dashboard.title}>{t("role.support")}</h1>
      <ul className={dashboard.list}>
        {destinations.map((item) => (
          <li key={item.to}>
            <Link to={item.to}>{t(item.labelKey)}</Link>
          </li>
        ))}
      </ul>
    </>
  );
}

/**
 * O painel de cada papel — os quatro escritos, um a um.
 *
 * Era um `switch` cujo `default` entregava ao suporte o painel do ALUNO: agenda
 * e financeiro de uma matricula que ele nao tem, vazios, sem dizer por que. Nao
 * houve vazamento — o painel do aluno so mostra o dado de quem o abre —, mas e
 * a mesma forma de defeito que deixou a rota administrativa aberta: um caminho
 * sem dono, escolhido por omissao.
 *
 * O conserto e o `Record<Role, ...>`, e nao um `case "support"` a mais. Com o
 * mapa total, o papel que o contrato acrescentar **nao compila** ate alguem
 * decidir qual painel ele ve; com mais um `case`, o proximo papel cairia calado
 * no `default` exatamente como este caiu. A alternativa recusada foi manter o
 * `switch` e lancar no `default`: ela troca a tela errada por uma tela quebrada
 * em producao, e adia a decisao para depois do deploy.
 *
 * Papel fora do enum nao chega aqui: a home so e montada dentro do `RoleGuard`
 * com `ANY_ROLE`, e `routes.test.tsx` prende `ANY_ROLE` ao enum inteiro.
 */
const ROLE_HOME: Record<Role, ComponentType> = {
  super_admin: AdminHome,
  teacher: TeacherHome,
  student: StudentHome,
  support: SupportHome,
};

function RoleHome({ me }: { readonly me: Me }) {
  const Home = ROLE_HOME[me.role];
  return <Home />;
}

function MfaRoute() {
  const session = useSession();
  if (session.isPending) return <Loading />;
  if (!session.me) return <Navigate to="/login" replace />;
  // Sessao ja plena nao tem desafio a resolver. Sem esta linha, digitar /mfa na
  // barra de endereco pedia o TOTP de novo a quem tinha acabado de passar por
  // ele — e pedido de codigo fora de hora e justamente o que treina a pessoa a
  // digitar o segundo fator em qualquer tela que peca.
  //
  // Recusada a alternativa de tirar `/mfa` da tabela e deixar so a gaveta
  // interna (`MfaGate`, que `Authenticated` monta): a rota precisa existir como
  // endereco proprio porque e para ela que a tela de entrada navega enquanto a
  // sessao esta incompleta. O desvio vai para a home do papel, o mesmo destino
  // que `MfaChallengePage` ja usa ao concluir a verificacao — as duas saidas
  // concordam, e nao ha corrida entre elas.
  if (session.state === "full") return <Navigate to={ROLE_HOME_PATH} replace />;
  if (session.state === "mfa_enrollment_required") return <MfaEnrollmentPage />;
  return <MfaChallengePage />;
}

function MfaGate({ state }: { readonly state: SessionState }) {
  if (state === "mfa_enrollment_required") return <MfaEnrollmentPage />;
  return <MfaChallengePage />;
}

function Authenticated({ children }: { readonly children: ReactNode }) {
  const { t } = useTranslation();
  const session = useSession();
  const location = useLocation();

  if (session.isPending) return <Loading />;
  if (session.isUnauthenticated || !session.me) {
    if (session.hasError) return <FullPageMessage>{t("error.loadFailed")}</FullPageMessage>;
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (session.state !== "full") return <MfaGate state={session.state as SessionState} />;

  return (
    <TermsGate me={session.me}>
      <AppShell me={session.me}>{children}</AppShell>
    </TermsGate>
  );
}

function GuestOnly({ children }: { readonly children: ReactNode }) {
  const session = useSession();
  const location = useLocation();

  if (session.isPending) return <Loading />;

  // Mesma decisao que a tela de entrada toma ao concluir o login, e nao um "/"
  // proprio. As duas correm juntas — a sessao entra no cache enquanto a
  // navegacao acontece —, e enquanto discordavam era sorte quem chegava
  // primeiro: o destino escolhido apos o login era atropelado por este desvio.
  if (session.me) {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={landingPathAfterLogin(from, session.me.role)} replace />;
  }

  return <>{children}</>;
}

function NotFound() {
  const { t } = useTranslation();
  return (<main><h1>{t("route.notFoundTitle")}</h1><p>{t("route.notFoundBody")}</p><Link to="/">{t("route.backHome")}</Link></main>);
}

function RoleHomeWrapper() {
  const session = useSession();
  if (!session.me) return null;
  return <RoleHome me={session.me} />;
}

/**
 * Rota que roda ANTES de existir papel a conferir, e nao rota cujo papel alguem
 * esqueceu de declarar. Sao duas, e a lista literal em `routes.test.tsx` tem dois
 * itens: `/mfa`, que existe justamente enquanto a sessao esta incompleta, e o
 * curinga de "nao encontrado", que responde igual para todo mundo.
 *
 * O valor e escrito a mao em cada uma. Nao existe o caso de omitir `allow` — o
 * tipo obriga a decidir —, so o de decidir por esta.
 */
const NO_ROLE_CHECK = "no-role-check";

/**
 * Espelha `AccessAuthenticated`: basta sessao.
 *
 * Restringir aqui o que o servidor libera criaria uma SEGUNDA politica de
 * autorizacao, e a divergencia apareceria como tela em branco sem que ninguem
 * soubesse qual das duas estava certa. Quando o servico e quem recorta o que
 * cada papel enxerga — gravacao, agenda, presenca —, a rota abre e a lista vem
 * vazia, que e a resposta correta e nao um defeito.
 */
const ANY_ROLE: readonly Role[] = ["super_admin", "teacher", "student", "support"];
const ADMIN: readonly Role[] = ["super_admin"];
const ADMIN_AND_TEACHER: readonly Role[] = ["super_admin", "teacher"];
const TEACHER: readonly Role[] = ["teacher"];
const STUDENT: readonly Role[] = ["student"];

interface AppRouteBase {
  readonly path: string;
  /**
   * O componente da rota mora na tabela, e a arvore de `<Route>` nasce dela.
   *
   * Antes eram duas listas — a tabela aqui e o JSX logo abaixo — e so a segunda
   * decidia o que renderizava. Rota acrescentada num lugar e nao no outro
   * passava batida por qualquer invariante escrito sobre a tabela; foi assim que
   * `/finance/pricing` chegou a producao sem portao de papel.
   */
  readonly Page: ComponentType;
}

export type AppRoute =
  | (AppRouteBase & { readonly guest: true })
  | (AppRouteBase & {
      readonly guest: false;
      readonly allow: readonly Role[] | typeof NO_ROLE_CHECK;
    });

/**
 * As rotas da SPA e os papeis que alcancam cada uma.
 *
 * **A matriz mora aqui, e nao em `nav.ts`.** `nav.ts` so conhece rota de menu:
 * `/students/:studentId`, `/payroll/:batchId`, `/attendance/:occurrenceId/adjust`
 * e as demais com parametro nunca aparecem nele — e sao justamente as que abrem
 * o registro de uma pessoa. Uma matriz derivada do menu deixaria essas de fora
 * exatamente como o menu ja as deixa.
 *
 * A intencao espelhada e a de `api/internal/app/policies.go`, que e a fonte de
 * verdade da autorizacao. Onde a politica do backend declara `Roles`, a lista
 * aqui e a mesma; onde ela e `AccessAuthenticated`, a lista aqui e `ANY_ROLE`.
 * Duas telas fogem dessa leitura literal e o motivo esta anotado nelas.
 *
 * Isto NAO substitui o servidor: quem recusa o dado continua sendo ele. O que o
 * portao evita e a tela de administracao — com os campos que a escola cobra e a
 * politica de inadimplencia que ela pratica — desenhada para quem so digitou a
 * URL, respondendo ao clique e falhando com erro generico.
 */
export const appRoutes: readonly AppRoute[] = [
  { path: "/login", guest: true, Page: LoginPage },
  { path: "/mfa", guest: false, allow: NO_ROLE_CHECK, Page: MfaRoute },

  // Agenda, sala, gravacao, reposicao e presenca: leitura autenticada, com o
  // recorte por pessoa feito no servico.
  { path: "/schedule", guest: false, allow: ANY_ROLE, Page: SchedulePage },
  { path: "/schedule/changes", guest: false, allow: ANY_ROLE, Page: ScheduleChangesPage },
  { path: "/room/:occurrenceId", guest: false, allow: ANY_ROLE, Page: RoomPage },
  { path: "/recordings", guest: false, allow: ANY_ROLE, Page: RecordingsPage },
  { path: "/makeup", guest: false, allow: ANY_ROLE, Page: MakeupPage },
  { path: "/attendance", guest: false, allow: ANY_ROLE, Page: AttendancePage },
  // Aceitar ou recusar reposicao e do professor: as duas operacoes que esta tela
  // existe para chamar sao `RoleTeacher` no contrato.
  { path: "/makeup/:makeupRequestId/respond", guest: false, allow: TEACHER, Page: MakeupRespondPage },
  // Ajuste de presenca e a operacao sensivel de Super Admin, e a tela e o
  // formulario dela.
  { path: "/attendance/:occurrenceId/adjust", guest: false, allow: ADMIN, Page: AttendanceAdjustPage },

  // A grade recorrente e cadastro. Primeira das duas telas que fecham mais do
  // que a leitura pediria: `listClassSeries` e autenticada, mas o criador que
  // esta na mesma tela pede `listTeachers` e `listEnrollments`, exclusivas de
  // Super Admin. Abrir para professor e aluno seria abrir um formulario que so
  // sabe responder 403.
  { path: "/series", guest: false, allow: ADMIN, Page: ClassSeriesPage },

  // Registro pedagogico de uma aula: quem da a aula e quem administra.
  { path: "/academic/occurrences/:occurrenceId/record", guest: false, allow: ADMIN_AND_TEACHER, Page: ClassRecordForm },
  // `listMyPerformance` e do aluno e so dele; nao existe versao desta tela para
  // outro papel — a visao de professor e de Super Admin e
  // `/students/:studentId/performance`, logo abaixo.
  { path: "/academic/performance", guest: false, allow: STUDENT, Page: StudentPerformance },
  { path: "/academic/settings", guest: false, allow: ADMIN, Page: AssessmentSettings },

  { path: "/materials", guest: false, allow: ANY_ROLE, Page: MaterialLibrary },
  { path: "/messages", guest: false, allow: ANY_ROLE, Page: Inbox },
  { path: "/messages/:conversationId", guest: false, allow: ANY_ROLE, Page: Inbox },
  { path: "/announcements", guest: false, allow: ANY_ROLE, Page: Announcements },
  { path: "/settings/reminders", guest: false, allow: ANY_ROLE, Page: ReminderPreferences },

  // Cadastro. `StudentAdminDetail` e `TeacherAdminDetail` carregam dado pessoal,
  // e nao ha caminho de professor ate eles.
  { path: "/students", guest: false, allow: ADMIN, Page: StudentsPage },
  { path: "/students/:studentId", guest: false, allow: ADMIN, Page: StudentDetailPage },
  { path: "/students/:studentId/performance", guest: false, allow: ADMIN_AND_TEACHER, Page: StudentPerformanceForStudent },
  { path: "/teachers", guest: false, allow: ADMIN, Page: TeachersPage },
  { path: "/teachers/:teacherId", guest: false, allow: ADMIN, Page: TeacherDetailPage },
  // Segunda tela que fecha mais que a leitura: `listPlans` e autenticada, mas a
  // pagina inteira e o cadastro de plano, e `createPlan` e de Super Admin.
  { path: "/plans", guest: false, allow: ADMIN, Page: PlansPage },
  { path: "/enrollments", guest: false, allow: ADMIN, Page: EnrollmentsPage },
  { path: "/my-enrollment", guest: false, allow: STUDENT, Page: MyEnrollmentPage },

  // Dinheiro. O portal e do aluno; o financeiro administrativo, o catalogo de
  // precos e a politica de inadimplencia sao de Super Admin.
  { path: "/billing", guest: false, allow: STUDENT, Page: StudentBilling },
  { path: "/finance", guest: false, allow: ADMIN, Page: AdminFinance },
  { path: "/finance/pricing", guest: false, allow: ADMIN, Page: PricingPage },
  // `getMyEarnings` e sobre o solicitante e nao tem parametro de professor:
  // nao existe versao administrativa desta tela.
  { path: "/earnings", guest: false, allow: TEACHER, Page: TeacherEarnings },
  { path: "/payroll", guest: false, allow: ADMIN, Page: PayrollBatchesPage },
  { path: "/payroll/:batchId", guest: false, allow: ADMIN, Page: PayrollBatchPage },
  { path: "/dashboards/executive", guest: false, allow: ADMIN, Page: ExecutiveDashboard },

  // A home aceita todo papel por obrigacao, e nao por generosidade: ela e o
  // destino de todo redirecionamento do portao, e um papel recusado aqui giraria
  // em ciclo. `RoleHome` e quem escolhe o painel.
  { path: ROLE_HOME_PATH, guest: false, allow: ANY_ROLE, Page: RoleHomeWrapper },
  { path: "*", guest: false, allow: NO_ROLE_CHECK, Page: NotFound },
];

export interface RoleMatrixAudit {
  /** Rota que declara papel inexistente ou lista vazia. Vazio e o estado correto. */
  readonly offenders: readonly string[];
  /** Rota dispensada por `NO_ROLE_CHECK`. Quem confere a lista pelo nome e o teste. */
  readonly withoutMatrix: readonly string[];
}

/**
 * A varredura da matriz de papeis, como FUNCAO — e nao como laco escrito dentro
 * do teste.
 *
 * O laco morava no proprio `it`, e o caso vizinho, que se anunciava como a prova
 * de que a varredura sabe acusar, reescrevia `allow.length === 0` em duas linhas
 * sobre um array literal e conferia que o `filter` recem-escrito devolvia um
 * item. Ele passava com este arquivo inteiro apagado: nao havia caminho pelo
 * qual codigo de producao pudesse quebra-lo. Prova falsa e pior que prova
 * ausente, porque a revisao seguinte confia nela e nao rele.
 *
 * Com a varredura aqui, o MESMO codigo que confere `appRoutes` e o que se prova
 * capaz de reprovar uma tabela sintetica. Apagar a funcao quebra os dois casos;
 * inverter o predicado quebra o negativo.
 *
 * `roles` entra por parametro em vez de ser lido do enum aqui porque `Role` e
 * importado como tipo neste arquivo: recebe-lo mantem a funcao pura e deixa o
 * teste passar tambem um enum sintetico quando precisar.
 */
export function auditRoleMatrix(
  routes: readonly AppRoute[],
  roles: readonly Role[],
): RoleMatrixAudit {
  const offenders: string[] = [];
  const withoutMatrix: string[] = [];

  for (const route of routes) {
    // `/login` roda fora da sessao: nao ha papel para conferir.
    if (route.guest) continue;

    if (route.allow === NO_ROLE_CHECK) {
      withoutMatrix.push(route.path);
      continue;
    }
    if (route.allow.length === 0) {
      offenders.push(`${route.path} declara lista vazia de papeis`);
    }
    for (const role of route.allow) {
      if (!roles.includes(role)) {
        offenders.push(`${route.path} declara papel inexistente: ${role}`);
      }
    }
  }

  return { offenders, withoutMatrix };
}

/**
 * Para onde ir depois de entrar.
 *
 * `from` foi gravado por `Authenticated` quando a sessao ANTERIOR caiu, e nada
 * liga aquela sessao a esta: saindo de `/earnings` como o professor e entrando
 * como a aluna, o caminho guardado levava a aluna de volta a tela de ganhos do
 * professor. Aqui `from` e tratado pelo que ele e — entrada vinda de fora,
 * conferida contra a mesma matriz que governa as rotas — e cai na home do papel
 * quando nao serve.
 *
 * Caminho que nao e rota nenhuma tambem cai na home: `from` chega do estado da
 * navegacao, e estado de navegacao nao merece mais confianca que a URL digitada.
 */
export function landingPathAfterLogin(from: string | undefined, role: Role | undefined): string {
  if (from === undefined || role === undefined) return ROLE_HOME_PATH;

  const route = appRoutes.find((candidate) => matchPath(candidate.path, from) !== null);
  if (route === undefined || route.guest || route.allow === NO_ROLE_CHECK) return ROLE_HOME_PATH;

  return route.allow.includes(role) ? from : ROLE_HOME_PATH;
}

function routeElement(route: AppRoute): ReactNode {
  const { Page } = route;

  if (route.guest) return <GuestOnly><Page /></GuestOnly>;
  if (route.allow === NO_ROLE_CHECK) return <Page />;

  return (
    <Authenticated>
      <RoleGuard allow={route.allow}><Page /></RoleGuard>
    </Authenticated>
  );
}

export function AppRoutes() {
  return (
    <Routes>
      {appRoutes.map((route) => (
        <Route key={route.path} path={route.path} element={routeElement(route)} />
      ))}
    </Routes>
  );
}
